import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse } from 'services/types'
import { BulkLinkageOrderAssetSettingsResponse, CreateOrderAssetSettingsRequest } from './types'

export const orderAssetSettingsApi = createApi({
  reducerPath: 'orderAssetSettingsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/engagement/'}`,
  }),
  tagTypes: ['OrderAssetSettingsList'],
  endpoints: (builder) => ({
    getOrderAssetSettingsList: builder.query<
      GenericResponse & {
        data: Array<{
          asset_types: number[]
          id: string
          max_value: number
          min_value: number
          name: string
        }>
      },
      { client_id: string; service_point_id?: string }
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/asset_settings/`,
        params: {
          service_point_id: data.service_point_id,
        },
        method: 'GET',
        data,
      }),
      providesTags: ['OrderAssetSettingsList'],
    }),
    createOrderAssetSettings: builder.mutation<
      GenericResponse & { id: string },
      CreateOrderAssetSettingsRequest
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/asset_settings/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['OrderAssetSettingsList'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          console.log(err)
        }
      },
    }),
    linkUnlinkOrderAssetSettingsToServicePoint: builder.mutation<
      GenericResponse & BulkLinkageOrderAssetSettingsResponse,
      {
        client_id: string
        asset_settings_id_list: Array<string>
        service_point_id: string
        action: 'link' | 'unlink'
      }
    >({
      query: ({ client_id, service_point_id, ...data }) => ({
        url: `clients/${client_id}/asset_settings/service_point/${service_point_id}/link_unlink_service_point/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['OrderAssetSettingsList'],
    }),
    bulkLinkUnlinkOrderAssetSettingsToServicePoint: builder.mutation<
      GenericResponse & BulkLinkageOrderAssetSettingsResponse,
      {
        client_id: string
        asset_settings_id_list: Array<string>
        service_point_id_list: Array<string>
        action: 'link' | 'unlink'
        overwrite: boolean
      }
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/asset_settings/bulk_link_unlink_service_point/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['OrderAssetSettingsList'],
    }),
  }),
})

export const {
  useGetOrderAssetSettingsListQuery,
  useCreateOrderAssetSettingsMutation,
  useLinkUnlinkOrderAssetSettingsToServicePointMutation,
  useBulkLinkUnlinkOrderAssetSettingsToServicePointMutation,
} = orderAssetSettingsApi
